import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PrivateParkCard from "../components/PrivateParkCard";
import Breadcrumbs from "../components/Breadcrumbs";
import { PRIVATE_PARKS } from "../data/privateParks";

const SITE_URL = "https://pawground.app";
const CANONICAL = `${SITE_URL}/private-parks`;
const TITLE = "Private Dog Parks — Rent a Fenced Yard by the Hour | Pawground";
const DESC =
  "Book a private, fully fenced yard for your dog by the hour. Founding hosts across the U.S. — no membership fees, you pay the host directly.";

/**
 * Static landing page for the Private Parks marketplace at /private-parks.
 *
 * Why this exists:
 *   - Mirrors the URL shape of other category hubs (/dog-parks, /dog-beaches)
 *     so the Private Parks category has its own indexable, prerenderable
 *     URL instead of living behind /search?category=private.
 *   - The pre-rendered HTML shell (see frontend/scripts/prerender.js) bakes
 *     in title, meta description, canonical, breadcrumb JSON-LD, and a
 *     plain-text fallback listing of every entry in PRIVATE_PARKS so
 *     crawlers can index the surface even before React boots.
 *   - Individual listing detail pages still live at /private-parks/{slug}.
 *
 * Data source: src/data/privateParks.js (the same registry used by the
 * Search page when category=private). Move to MongoDB when we grow past
 * a handful of hosts.
 */
const PrivateParksIndex = () => {
  // Per-route head tags. Server-rendered HTML comes from prerender.js;
  // this updates after React boots so client navigation between routes
  // stays accurate too. Pattern mirrors Search.jsx and PrivateParkHost.jsx.
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (sel, attr, value) => {
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement(sel.startsWith("link") ? "link" : "meta");
        if (sel.startsWith('meta[name="')) {
          el.setAttribute("name", sel.match(/name="([^"]+)"/)[1]);
        } else if (sel.startsWith('meta[property="')) {
          el.setAttribute("property", sel.match(/property="([^"]+)"/)[1]);
        } else if (sel.startsWith('link[rel="')) {
          el.setAttribute("rel", sel.match(/rel="([^"]+)"/)[1]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", DESC);
    setMeta('link[rel="canonical"]', "href", CANONICAL);
    setMeta('meta[property="og:title"]', "content", TITLE);
    setMeta('meta[property="og:description"]', "content", DESC);
    setMeta('meta[property="og:url"]', "content", CANONICAL);
    setMeta('meta[property="og:type"]', "content", "website");
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <Breadcrumbs items={[{ name: "Private Parks", to: "/private-parks" }]} />

        <div className="mt-2">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-forest/10 text-brand-forest text-xs font-semibold tracking-wide uppercase"
            data-testid="private-parks-eyebrow"
          >
            🏡 Private Parks
          </span>
          <h1
            data-testid="private-parks-h1"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-brand-ink mt-3 leading-tight"
          >
            Rent a private, fenced yard by the hour.
          </h1>
          <p className="text-base sm:text-lg text-brand-ink/70 mt-3 leading-relaxed">
            Skip the crowded public park. Book a hand-vetted private yard for a calm,
            controlled romp — your dog gets the whole space, you pay the host directly,
            no membership fees.
          </p>
        </div>

        <div className="flex items-baseline justify-between mt-8">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-brand-ink">
            Available now
          </h2>
          <span className="text-sm text-brand-ink/60" data-testid="private-parks-count">
            {PRIVATE_PARKS.length} listing{PRIVATE_PARKS.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="mt-4 space-y-3" data-testid="private-parks-list">
          {PRIVATE_PARKS.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center border border-brand-border-warm">
              <div className="font-heading text-lg font-semibold text-brand-ink">
                No private parks yet
              </div>
              <p className="text-sm text-brand-ink/60 mt-2 leading-relaxed">
                We're onboarding our first founding hosts. Want to list your yard?
              </p>
            </div>
          ) : (
            PRIVATE_PARKS.map((p) => <PrivateParkCard key={p.slug} park={p} />)
          )}
        </div>

        {/* Host CTA — the value of this page for non-renters is the
            sign-up funnel into the host waitlist. Keep it visible
            without overpowering the listings above. */}
        <div className="mt-10 bg-white rounded-2xl border border-brand-border-warm p-6 sm:p-8">
          <p className="text-xs font-semibold tracking-wider uppercase text-brand-forest">
            Got a fenced yard?
          </p>
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-brand-ink mt-2">
            Earn extra income hosting dogs on Pawground.
          </h3>
          <p className="text-sm sm:text-base text-brand-ink/70 mt-2 leading-relaxed">
            No membership fees for guests, flat booking fee for hosts. Founding hosts lock
            in a permanently lower rate.
          </p>
          <Link
            to="/private-parks/host"
            data-testid="private-parks-host-cta"
            className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-brand-forest hover:bg-[#3d6749] text-white text-sm font-semibold transition-all active:scale-[0.97]"
          >
            List your yard
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivateParksIndex;
